(ns ncr.charts
  "hic sunt dracones, this namespace is under active development, contains ugly code and is subject to change"
  (:require
   ["@patternfly/react-core/dist/esm/helpers/resizeObserver" :refer [getResizeObserver]]
   ["react" :as react]
   [ncr.cube :as cube]
   [goog.dom :as gdom]
   [goog.dom.TagName :as TagName]
   [goog.math :as gm]
   [goog.style :as gs]
   [ncr.fragment.props.money :as m]
   [ncr.reports.explain :as re]
   [ncr.reports.common :as rc]
   [ncr.etc.patternfly-charts :as c]
   [ncr.etc.pf :as pf]
   [uix.core.alpha :as uix]
   [ncr.widgets.grid :as grid]
   [ncr.etc.util :as u]
   [ncr.symbolic :as sym]
   [ncr.widgets.core :as w]
   [tick.core :as t]))

(def ^:private report-types {:revenue "Revenue" :costs "Costs" :pnl "PnL"})

(defn- get-fn [path f]
  (if path f identity))

(defn from-path' [report {:keys [report-path cube-path sym-path]}]
  (some-> report
    (get-in report-path)
    ((get-fn cube-path #(cube/cell % cube-path true)))
    ((get-fn sym-path #(sym/get-path % sym-path)))))

(defn- with-month [paths month]
  (update paths :cube-path conj month))

(defn- with-kind [paths kind]
  (update paths :sym-path (partial into [kind])))

(defn- get-value
  ([report path kind month]
   (get-value report (with-kind (with-month path month) kind)))
  ([report path default]
   (if-let [^js v (:value (from-path' report path))]
     (.toNumber v)
     default))
  ([report path]
   (get-value report path 0)))

(defn- get-base-and-delta [actual estimate]
  (let [estimate (or estimate actual)]
    [(min estimate actual) (- estimate actual)]))

(defn- calculate-deltas [report path]
  (reduce
    (fn [[a d] idx]
      (let [m (inc idx)
            month-path (with-month path m)
            actual-path (with-kind month-path :actual)
            estimated-path (with-kind month-path :estimated)
            [base delta] (get-base-and-delta
                           (get-value report actual-path)
                           (get-value report estimated-path nil))
            overflow? (neg? delta)
            actual-value (:value (from-path' report actual-path))
            estimated-value (:value (from-path' report estimated-path))]
        [(conj a {:x m :y base :overflow overflow?
                  "actualValue" actual-value "estimatedValue" estimated-value
                  "path" actual-path "bignumValue" actual-value})
         (conj d {:x m :y (abs delta) :overflow overflow?
                  "actualValue" actual-value "estimatedValue" estimated-value
                  "path" estimated-path "bignumValue" estimated-value})]))
    [[] []]
    (range 0 12)))

(defn- if-overflow [^js data a b]
  (if (.-overflow (.-datum data)) a b))

; for colors see https://www.patternfly.org/charts/colors-for-charts

(def ^:private stroke-width 0)
(def ^:private padding-left 75)
(def ^:private padding-right 10)

(defn- datapoint-key [^js p]
  (str (.-eventKey p) (.-childName p)))

(def ^:private label-width 200)

(def ^:private bar-label-label
  (react/createElement
    (uix/as-react
      (fn [{:keys [x y ^js datum]}]
        (let [px (- 10 (/ label-width 2))]
          [:g {:transform (str "translate(" x "," y ")")}
           [:text {:x 0 :y -15 :font-size 16 :text-anchor "middle" :fill "white" :font-weight "bold"} (.-name datum)]
           [:text {:x px :y 5 :text-anchor "start" :fill "white"} "Actual"]
           [:text {:x (- px) :y 5 :text-anchor "end" :fill "white"} (m/to-string (.-actualValue datum))]
           [:text {:x px :y 22 :text-anchor "start" :fill "#ddd"} "Estimated"]
           [:text {:x (- px) :y 22 :text-anchor "end" :fill "#ddd"} (m/to-string (.-estimatedValue datum) "-")]])))))

(def ^:private bar-label-component
  (react/createElement
    (uix/as-react
      (fn [props]
        (when (:label_active props)
          [c/tooltip
           (assoc props
             :flyout-height 60
             :flyout-width label-width
             :label-component bar-label-label)])))))

(defn- bar-label-fn [^js data]
  (str (.. data -datum -name) " " (.. data -datum -bignumValue)))

(def ^:private voronoi-label-component
  (c/tooltip {:dx (fn [^js data] (.-offsX ^js (last (.-activePoints data))))}))

(defn- voronoi-label-fn [^js data]
  (when-let [pnl (and (= "pnl-points" (.. data -datum -childName)) (m/to-string (.. data -datum -bignumValue) nil))]
    (str (.. data -datum -name) " " pnl)))

(defn- path-js->clj [^js path]
  (->> path
    js->clj
    (map (fn [[k v]] [(keyword k) (mapv (fn [v] (if (string? v) (keyword v) v)) v)]))
    (into {})))

(defn- month-indicator [{:keys [month paddings width height]}]
  (let [{:keys [bottom top left right]} paddings
        pad-x 2
        pad-y 20
        width (/ (+ pad-x (- width left right)) 12)
        x (+ left (* width month) pad-x)
        width (- width pad-x pad-x)
        height (+ (- height bottom top) pad-y)
        y (- top pad-y)]
    [:<>
     [:defs
      [:linearGradient#1 {:x1 0.5 :y1 0 :x2 0.5 :y2 1}
       [:stop {:offset "0%" :stop-color "#8BC1F788"}]
       [:stop {:offset "100%" :stop-color "#8BC1F788"}]]]
     [:rect {:x x :width width :y y :height height
             :opacity 0.9 :fill "url(#1)" :stroke "none"}]]))

(defn c-overview [{:keys [report paths on-click width height active-month]}]
  (let [*container-ref (uix/ref)
        *width (uix/state width)
        width @*width
        height (or height 450)
        used-width (uix/memo (fn [] (- width padding-left padding-right)) [width])
        bar-width (uix/memo (fn [] (-> used-width (* 0.6) (/ 24))) [used-width])
        label-x-offs (uix/memo (fn [] (/ bar-width 2)) [bar-width])
        resize! (uix/callback (fn [] (reset! *width (.-clientWidth ^js @*container-ref))) [])
        *active-points (uix/ref {})
        [revenue costs pnl] paths
        revenue-data (uix/memo #(calculate-deltas report revenue) [report revenue])
        costs-data (uix/memo #(calculate-deltas report costs) [report costs])
        pnl-data (uix/memo
                   #(map (fn [m]
                           (let [^js rev (get-value report (with-month (with-kind revenue :actual) m))
                                 is-actual? (pos? rev)
                                 path (with-month (with-kind pnl (if is-actual? :actual :estimated)) m)]
                             {:name (str "PnL" (when-not is-actual? " estimated"))
                              :x m :y (get-value report path) :is-actual? is-actual?
                              "offsX" 0 "path" path "bignumValue" (:value (from-path' report path))})) (range 1 13))
                   [report pnl revenue])
        on-click! (uix/memo
                    (fn []
                      (when on-click
                        (fn [_ ^js data] (on-click (path-js->clj (.. data -datum -path))))))
                    [on-click])
        on-click-voronoi! (uix/memo
                            (fn []
                              (when on-click
                                #(let [^js active-point (some-> @*active-points vals first)]
                                   (when active-point (on-click (path-js->clj (.-path active-point)))))))
                            [on-click @*active-points])
        style-data (uix/memo
                     (fn []
                       (merge {:stroke-width stroke-width} (when on-click! {:cursor "pointer"})))
                     [on-click!])
        [pnl-data-before pnl-data-after] (split-with :is-actual? pnl-data)
        pnl-data-before-last (last pnl-data-before)
        pnl-data-after (conj pnl-data-after pnl-data-before-last)]

    (uix/layout-effect!
      (fn []
        (let [unobserve (getResizeObserver @*container-ref resize!)]
          (resize!)
          unobserve))
      [resize!])

    [:div {:ref *container-ref}
     [:svg {:aria-hidden "true" :height "0" :width "0" :style {:display "block"}}
      [:defs
       [:pattern#ncr-pattern-revenue-estimate
        {:patternContentUnits "userSpaceOnUse" :patternUnits "userSpaceOnUse" :patternTransform "scale(1.0 1.0)" :height 5 :width 5 :x 0 :y 0}
        [:g
         [:rect {:x 0 :y 0 :width 5.5 :height 5.5 :fill "#23511E"}]
         [:path {:d "M 0 0 L 5 5 M 4.5 -0.5 L 5.5 0.5 M -0.5 4.5 L 0.5 5.5" :stroke "#38812F" :stroke-width 2}]]]
       [:pattern#ncr-pattern-costs-estimate
        {:patternContentUnits "userSpaceOnUse" :patternUnits "userSpaceOnUse" :patternTransform "scale(1.0 1.0)" :height 5 :width 5 :x 0 :y 0}
        [:rect {:x 0 :y 0 :width 5.5 :height 5.5 :fill "#F0AB00"}]
        [:path {:d "M 0 0 L 5 5 M 4.5 -0.5 L 5.5 0.5 M -0.5 4.5 L 0.5 5.5" :stroke "#A30000" :stroke-width 2}]]]]
     (when width
       (let [default-props
             {:bar-width bar-width
              :label-component bar-label-component
              :labels bar-label-fn
              :events [{:target "data"
                        :eventHandlers
                        {:onClick on-click!
                         :onMouseOver
                         (fn []
                           (clj->js [{:target "labels",
                                      :mutation #(clj->js {:active true :label_active true})}]))
                         :onMouseOut
                         (fn []
                           (clj->js [{:target "labels",
                                      :mutation #(clj->js {:active false :label_active false})}]))}}]}
             paddings {:bottom 30
                       :top 10
                       :left padding-left
                       :right padding-right}]
         [c/chart
          {:padding paddings
           :domain-padding #js {:x 40 :y 25}
           :height height
           :width width
           :container-component
           (c/voronoi-container
             {:labels voronoi-label-fn
              :label-component voronoi-label-component
              :radius label-x-offs
              :on-activated (fn [points _props]
                              (swap! *active-points
                                (fn [active]
                                  (->> points
                                    (filter (fn [^js p] (= "pnl-points" (.-childName p))))
                                    (map (fn [^js p] [(datapoint-key p) p]))
                                    (into {})
                                    (merge active)))))
              :on-deactivated (fn [points _props]
                                (swap! *active-points
                                  (fn [active]
                                    (reduce dissoc active (map (fn [^js p] (datapoint-key p)) points)))))
              :constrain-to-visible-area true
              :events {:onClick on-click-voronoi!}})}

          (when active-month
            [month-indicator {:paddings paddings :width width :height height :month (dec active-month)}])

          (c/axis {:dependent-axis true :show-grid true :cross-axis true :standalone false :tick-count 7})
          (c/axis {:cross-axis true
                   :standalone false
                   :tick-values (map #(get rc/months %) (range 0 12))
                   :style {:tick-labels
                           {:font-size (fn [^js args] (if (= active-month (.-tickValue args)) 16 14))
                            :fill (fn [^js args] (if (= active-month (.-tickValue args)) "var(--pf-global--primary-color--100)" "gray"))
                            :font-weight (fn [^js args] (if (= active-month (.-tickValue args)) "bold" "normal"))}}})

          (c/group
            {:offset bar-width}
            (let [[actual estimated] revenue-data]
              (c/stack
                (c/bar
                  (merge default-props
                    {:name "revenue"
                     :style {:data
                             (merge style-data
                               {:fill "#38812F" :stroke "#fff"})}
                     :data (map #(assoc % :name "Revenue" "offsX" (- label-x-offs)) actual)}))
                (c/bar
                  (merge default-props
                    {:name "revenue-estimated"
                     :style {:data (merge style-data
                                     {:fill #(if-overflow % "url(\"#ncr-pattern-revenue-estimate\")" "#b7d9b5")
                                      :stroke "#fff"})}
                     :data (map #(assoc % :name "Revenue" "offsX" (- label-x-offs)) estimated)}))))
            (let [[actual estimated] costs-data]
              (c/stack
                (c/bar
                  (merge default-props
                    {:name "costs"
                     :style {:data (merge style-data {:fill "#A30000" :stroke "#fff"})}
                     :data (map #(assoc % :name "Costs" "offsX" label-x-offs) actual)}))
                (c/bar
                  (merge default-props
                    {:name "costs-estimated"
                     :style {:data (merge style-data
                                     {:fill #(if-overflow % "url(\"#ncr-pattern-costs-estimate\")" "#f3c0b7")
                                      :stroke "#fff"})}
                     :data (map #(assoc % :name "Costs" "offsX" label-x-offs) estimated)})))))
          (c/line {:style {:data {:stroke "#5752D1"}} :name "pnl-before" :data pnl-data-before})
          (c/line {:style {:data {:stroke "#5752D1" :stroke-dasharray "4"}} :name "pnl-after" :data pnl-data-after})
          (c/scatter
            {:size 6
             :style {:data (merge {:fill "#8481DD" :stroke "#5752D1" :stroke-width 1}
                             (when on-click-voronoi! {:cursor "pointer"}))}
             :name "pnl-points" :data pnl-data})]))]))

(defn- c-total-entry [{:keys [class value custom-class]}]
  [:td {:class [:ncr-c-total__value class (or custom-class (if (neg? value) :ncr-c-total__expense :ncr-c-total__income))]} (m/to-string value)])

(defn- c-delta-entry [{:keys [value type]}]
  [:td {:class [:ncr-c-total__value]}
   [w/c-mdi-icon {:icon (if (neg? value) :triangle-down :triangle)
                  :class (cond
                           (and (= type :expense) (neg? value)) :ncr-c-total__income
                           (= type :expense) :ncr-c-total__expense
                           (neg? value) :ncr-c-total__expense
                           :else :ncr-c-total__income)}]
   (m/to-string (abs value))])

(defn- c-actual-estimated-forecast [{:keys [report path current-month type]}]
  (let [actual-path (with-month (with-kind path :actual) :total)
        estimated-path (with-month (with-kind path :estimated) :total)
        class (case type
                :income :ncr-c-total__income
                :expense :ncr-c-total__expense
                :pnl nil)
        [actual ytd end]
        (reduce
          (fn [[a y e] m]
            (let [get-value #(->> m inc
                               (with-month (with-kind path %))
                               (get-value report))
                  [am em] (map get-value [:actual :estimated])]
              (if (<= m current-month)
                [(+ a am) (+ y em) e]
                [a y (+ e em)])))
          [0 0 0] (range 0 12))]
    [:<>
     [c-total-entry {:class :ncr-c-total__actual :custom-class class :value (get-value report actual-path)}]
     [c-total-entry {:class :ncr-c-total__estimated :custom-class class :value (get-value report estimated-path)}]
     [:td {:class :ncr-c-chart__spacer}]
     [c-total-entry {:class :ncr-c-total__actual :custom-class class :value actual}]
     [c-delta-entry {:value (- actual ytd) :type type}]
     [c-total-entry {:class :ncr-c-total__estimated :custom-class class :value ytd}]
     [:td {:class :ncr-c-chart__spacer}]
     [c-total-entry {:class :ncr-c-total__forecast :custom-class class :value (+ actual end)}]
     [c-total-entry {:class :ncr-c-total__remaining :custom-class class :value end}]]))

(def ^:private current-month (-> (t/today) t/month t/int dec (max 0)))

(defn- c-totals [{:keys [report paths month on-change-month]}]
  (let [[revenue costs pnl] paths]
    [:table {:class :ncr-c-chart__totals}
     [:tbody
      [:tr {:class :ncr-c-chart__header_main} [:th]
       [:th {:class :ncr-c-chart__spacer}]
       [:th {:col-span 2} "Total"]
       [:th {:class :ncr-c-chart__spacer}]
       [:th {:col-span 3 :class :ncr-c-chart__header__with_controls}
        [:div
         [w/c-toolbar-button
          {:icon :arrow-left-thick
           :on-click #(on-change-month (max 0 (dec month)))
           :tooltip "Previous month"}]
         [:div
          [w/c-toolbar-button
           {:icon :calendar-month-outline
            :on-click #(on-change-month current-month)
            :tooltip "Current month"}]
          (str (get rc/months-long month) " YTD")]
         [w/c-toolbar-button
          {:icon :arrow-right-thick
           :on-click #(on-change-month (min 11 (inc month)))
           :tooltip "Next month"}]]]
       [:th {:class :ncr-c-chart__spacer}]
       [:th {:col-span 2} "Year End Projection"]]
      [:tr [:th]
       [:th {:class :ncr-c-chart__spacer}]
       [:th "Actual"]
       [:th "Planned"]
       [:th {:class :ncr-c-chart__spacer}]
       [:th "Actual"]
       [:th "Delta"]
       [:th "Planned"]
       [:th {:class :ncr-c-chart__spacer}]
       [:th "Projection"]
       [:th "Remaining Plan"]]
      [:tr {:class :ncr-c-chart__totals-revenue}
       [:td "Revenue"]
       [:td {:class :ncr-c-chart__spacer}]
       [c-actual-estimated-forecast {:report report :path revenue :current-month month :type :income}]]
      [:tr {:class :ncr-c-chart__totals-costs}
       [:td "Costs"]
       [:td {:class :ncr-c-chart__spacer}]
       [c-actual-estimated-forecast {:report report :path costs :current-month month :type :expense}]]
      [:tr {:class :ncr-c-chart__totals-pnl}
       [:td "PnL"]
       [:td {:class :ncr-c-chart__spacer}]
       [c-actual-estimated-forecast {:report report :path pnl :current-month month :type :pnl}]]]]))

(def ^:private container-margin -10)

(defn- center-popover [popover anchor get-left-top]
  (let [^js b (gs/getBounds anchor)
        ^js pb (gs/getBounds popover)
        [left top] (get-left-top b pb)
        ^js r (u/fit-rect
                (gm/Rect. left top (.-width pb) (.-height pb))
                (.createFromBox ^js gm/Rect
                  (.expand (gs/getVisibleRectForElement popover)
                    container-margin container-margin container-margin container-margin)))]
    (gs/setPosition popover (.-left r) (.-top r))))

(defn c-charts-button [{:keys [report paths header]}]
  (let [*anchor (uix/state nil)
        anchor @*anchor
        visible? (boolean anchor)
        *ref (uix/state nil)
        ref @*ref
        *append-to (uix/state nil)
        append-to @*append-to
        *width (uix/state nil)
        *selected-month (uix/state current-month)
        width @*width
        selected-month @*selected-month]

    (uix/layout-effect!
      (fn []
        (when anchor
          (reset! *width (* 0.75 (.-width (gs/getBounds anchor))))))
      [anchor])

    (uix/layout-effect!
      (fn []
        (when (and anchor ref width)
          (center-popover
            (gdom/getAncestorByTagNameAndClass ref TagName/DIV "pf-c-popover")
            anchor
            (fn [b pb]
              [(- (.-left b) (/ (- width (.-width b)) 2))
               (- (.-top b) (/ (- (.-height pb) (.-height b)) 2))]))))
      [anchor ref width])

    (uix/layout-effect!
      (fn []
        (when (and anchor (nil? append-to))
          (reset! *append-to (gdom/getAncestorByTagNameAndClass anchor TagName/TBODY ""))))
      [anchor append-to])

    [:span
     {:on-click (u/isolate* (fn [_]))} ; prevent click-through
     ; This is a workaround for the fact that the popover is not a child of the button or its parent element.
     ; Otherwise, clicking anywhere in the popover would open/close the parent element,
     ; i.e., the parent cell in the Reporting Table.
     [pf/c-popover
      {:is-visible visible?
       :should-close #(reset! *anchor nil)
       :has-auto-width true
       :class-name :c-chart
       :header-content
       [:div {:class :ncr-c-chart__totals_header}
        header
        [c-totals {:paths paths :report report :month selected-month :on-change-month #(reset! *selected-month %)}]]
       ; :append-to append-to
       :hide-on-outside-click true
       :body-content
       [:div {:ref (fn [node] (reset! *ref node))}
        (when width
          [c-overview
           {:report report
            :paths paths
            :width width
            :active-month (inc selected-month)
            :on-click #(reset! *selected-month (dec (last (:cube-path %))))}])]}]
     [grid/tools
      [:chart-bar
       (u/isolate*
         (fn [^js e]
           (reset! *selected-month current-month)
           (reset! *anchor (gdom/getAncestorByTagNameAndClass (.-target e)
                             TagName/MAIN "pf-c-page__main"))))]]]))

(defn c-header-with-experimental-charts-button [{:keys [header] :as props}]
  [:span.ncr-grid-heading
   header
   [c-charts-button props]])

(def ^:private revenue-path {:report-path [:cashflow :revenue] :cube-path [:total] :sym-path [:income]})
(def ^:private costs-path {:report-path [:cashflow :costs] :cube-path [:total] :sym-path [:expense]})
(defn- get-pnl-path [month] {:report-path [:cashflow :pnl] :cube-path (if month [(inc month)] []) :sym-path []})

(def ^:private paths [revenue-path costs-path (get-pnl-path nil)])

(defn c-overview-and-explain [{:keys [report fragments labels ignore-label? heading height]}]
  (let [selected-path (uix/state (get-pnl-path current-month))
        selected-month (some-> @selected-path :cube-path last)
        open-sections (uix/state {:actual-open? true :estimated-open? true})]
    [pf/c-card {:class :ncr-c-overview-and-explain :is-compact true}
     [pf/c-card-header
      [:div {:class :ncr-c-chart__totals_header} (or heading "Overview")
       [c-totals {:paths paths :report report :month (dec selected-month)
                  :on-change-month #(reset! selected-path (get-pnl-path %))}]]]
     [pf/c-card-body
      [c-overview {:report report :paths paths :height (or height 200)
                   :active-month selected-month
                   :on-click #(reset! selected-path %)}]]
     (when-let [{:keys [report-path] :as selected-path} @selected-path]
       (let [actual-path (assoc-in selected-path [:sym-path 0] :actual)
             estimated-path (assoc-in selected-path [:sym-path 0] :estimated)
             {:keys [estimated-open? actual-open?]} @open-sections
             params {:report report :fragments fragments
                     :labels (or labels (get report :labels {}))
                     :ignore-label? (or ignore-label? (constantly false))}]
         [pf/c-card-body
          [pf/c-title {:heading-level :h2}
           (str (get rc/months-long (dec selected-month)) " " (get report-types (last report-path)))]
          [grid/extension-error-boundary
           [:<>
            [pf/c-expandable-section
             {:on-toggle #(swap! open-sections assoc :estimated-open? %)
              :is-expanded estimated-open?
              :toggle-content [pf/c-title {:heading-level :h3} "Estimated"]}
             [re/c-explain-plain params {:path estimated-path}]]
            [pf/c-expandable-section
             {:on-toggle #(swap! open-sections assoc :actual-open? %)
              :is-expanded actual-open?
              :toggle-content [pf/c-title {:heading-level :h3} "Actual"]}
             [re/c-explain-plain params {:path actual-path}]]]]]))]))
